import { useCallback, useMemo } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import { linkDocument } from '@/api/generated';
import { useToast } from '@/components/ui/useToast';
import { navigation } from '@/lib/navigation';

import { useInvalidateDocuments } from './useDocuments';

/**
 * Open and Download (P12, PB9), shared by the row menu and the drawer.
 *
 * Both ask the backend for a fresh signed link at the moment of the click -
 * it lives 900 seconds, so nothing is fetched ahead of time - and hand it to
 * the browser. Open wants a new tab, and a popup blocker allows one only
 * when it is opened inside the click itself; so the tab is opened blank
 * first and pointed at the link once it arrives. If the request is refused
 * the blank tab is closed again and the reason toasted. Download assigns
 * the attachment link in this tab: the backend answers it with a
 * `Content-Disposition` that makes the browser save the file and stay.
 *
 * A refusal (no clean file yet, the document removed) means the row on
 * screen was out of date, so the library is asked for again (P14).
 */
export function useDocumentLink() {
  const toast = useToast();
  const invalidate = useInvalidateDocuments();

  const fetchLink = useCallback(
    async (id: string, disposition: 'inline' | 'attachment'): Promise<string | null> => {
      try {
        const reply = await linkDocument({
          path: { document_id: id },
          query: { disposition },
          throwOnError: true,
        });
        return unwrap(reply.data).url;
      } catch (error) {
        toast.show({
          message:
            (error instanceof ApiError ? error.serverMessage : null) ??
            'The file could not be reached. Please try again.',
          severity: 'error',
        });
        // Only a 409 or a 404 says the row on screen was wrong; a network
        // failure or a 5xx says nothing about it.
        if (error instanceof ApiError && (error.status === 409 || error.status === 404))
          await invalidate();
        return null;
      }
    },
    [toast, invalidate],
  );

  const open = useCallback(
    async (id: string) => {
      // Opened now, inside the click, so the blocker lets it through; severed
      // from this window before it is pointed anywhere else.
      const tab = window.open('about:blank', '_blank');
      if (tab) tab.opener = null;
      const url = await fetchLink(id, 'inline');
      if (!url) {
        tab?.close();
        return;
      }
      if (tab) tab.location.href = url;
      // The blank tab was blocked: try once more with the link itself, which
      // some blockers allow for a URL they can see.
      else window.open(url, '_blank', 'noopener');
    },
    [fetchLink],
  );

  const download = useCallback(
    async (id: string) => {
      const url = await fetchLink(id, 'attachment');
      if (url) navigation.assign(url);
    },
    [fetchLink],
  );

  return useMemo(() => ({ open, download }), [open, download]);
}
